import { useState, useCallback } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '@/services/api'
import {
  Card, Table, Pagination, Input, Select, Modal, Button, ConfidenceBadge, DetectionAlertCell,
} from '@/components/ui'
import { DEFAULT_PAGE_SIZE } from '@spoofio/shared'
import { formatListTimestamp } from '@/utils/format-date'
import type { DetectionResponse, AdminOrganizationResponse } from '@spoofio/shared/types'

export function AdminDetectionsPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const orgParam = searchParams.get('orgId') ?? ''
  const canaryParam = searchParams.get('canaryId') ?? ''
  const dateFromParam = searchParams.get('dateFrom') ?? ''
  const dateToParam = searchParams.get('dateTo') ?? ''
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)

  const [orgFilter, setOrgFilter] = useState(orgParam)
  const [canaryFilter, setCanaryFilter] = useState(canaryParam)
  const [dateFrom, setDateFrom] = useState(dateFromParam)
  const [dateTo, setDateTo] = useState(dateToParam)
  const [currentPage, setCurrentPage] = useState(pageParam)

  const [resendDetection, setResendDetection] = useState<DetectionResponse | null>(null)
  const [resendRecipients, setResendRecipients] = useState('')

  const syncParams = useCallback(
    (params: Record<string, string>) => {
      const sp = new URLSearchParams()
      for (const [k, v] of Object.entries(params)) {
        if (v) sp.set(k, v)
      }
      setSearchParams(sp)
    },
    [setSearchParams]
  )

  const updateFilters = useCallback(
    (overrides: Record<string, string>) => {
      const state = {
        orgId: orgFilter, canaryId: canaryFilter,
        dateFrom, dateTo, page: '1',
        ...overrides,
      }
      setCurrentPage(1)
      syncParams(state)
    },
    [orgFilter, canaryFilter, dateFrom, dateTo, syncParams]
  )

  const offset = (currentPage - 1) * DEFAULT_PAGE_SIZE

  const { data: orgsData } = useQuery({
    queryKey: ['admin-organizations-dropdown'],
    queryFn: () => api.adminListOrganizations({ limit: 200 }),
    staleTime: 60_000,
  })

  const { data: canariesData } = useQuery({
    queryKey: ['admin-canaries-dropdown', orgFilter],
    queryFn: () =>
      orgFilter
        ? api.adminListOrgCanaries(orgFilter, { limit: 200 })
        : api.adminListCanaries({ limit: 200 }),
    staleTime: 60_000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-detections', orgFilter, canaryFilter, dateFrom, dateTo, offset],
    queryFn: () =>
      api.adminListDetections({
        orgId: orgFilter || undefined,
        canaryId: canaryFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
      }),
    staleTime: 30_000,
  })

  const resendAlert = useMutation({
    mutationFn: () => {
      if (!resendDetection) return Promise.reject(new Error('No detection selected'))
      const recipients = resendRecipients
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean)
      return api.adminResendAlert(
        resendDetection.id,
        recipients.length > 0 ? recipients : undefined
      )
    },
    onSuccess: () => {
      setResendDetection(null)
      setResendRecipients('')
    },
  })

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    syncParams({
      orgId: orgFilter, canaryId: canaryFilter,
      dateFrom, dateTo, page: String(page),
    })
  }

  const orgOptions = [
    { value: '', label: 'All Organizations' },
    ...(orgsData?.data.map((o: AdminOrganizationResponse) => ({
      value: o.id,
      label: o.name,
    })) ?? []),
  ]

  const canaryOptions = [
    { value: '', label: 'All Canaries' },
    ...(canariesData?.data.map((c) => ({
      value: c.id,
      label: c.name,
    })) ?? []),
  ]

  const columns = [
    {
      key: 'detectedAt',
      header: 'Detected At',
      render: (d: DetectionResponse) => formatListTimestamp(d.detectedAt),
    },
    {
      key: 'fakeFqdn',
      header: 'Fake FQDN',
      render: (d: DetectionResponse) => (
        <span className="font-mono text-sm">{d.fakeFqdn}</span>
      ),
    },
    {
      key: 'organizationName',
      header: 'Organization',
      render: (d: DetectionResponse) => d.organizationName || '-',
    },
    {
      key: 'canaryName',
      header: 'Canary',
      render: (d: DetectionResponse) => (
        <span className="text-primary-600">{d.canaryName}</span>
      ),
    },
    {
      key: 'confidenceScore',
      header: 'Confidence',
      render: (d: DetectionResponse) => <ConfidenceBadge score={d.confidenceScore} />,
    },
    {
      key: 'alertSent',
      header: 'Alert',
      render: (d: DetectionResponse) => (
        <DetectionAlertCell alertSent={d.alertSent} screenshotUrl={d.screenshotUrl} />
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (d: DetectionResponse) => (
        <div onClick={(e) => { e.stopPropagation() }}>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setResendDetection(d) }}
          >
            Resend Alert
          </Button>
        </div>
      ),
    },
  ]

  const totalPages = data ? Math.ceil(data.total / DEFAULT_PAGE_SIZE) : 1

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Detections</h1>

      <Card>
        <div className="flex flex-wrap gap-4 mb-4">
          <Select
            label="Organization"
            value={orgFilter}
            onChange={(e) => {
              setOrgFilter(e.target.value)
              setCanaryFilter('')
              updateFilters({ orgId: e.target.value, canaryId: '' })
            }}
            options={orgOptions}
            className="w-48"
          />
          <Select
            label="Canary"
            value={canaryFilter}
            onChange={(e) => {
              setCanaryFilter(e.target.value)
              updateFilters({ canaryId: e.target.value })
            }}
            options={canaryOptions}
            className="w-48"
          />
          <Input
            label="Date From"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value)
              updateFilters({ dateFrom: e.target.value })
            }}
            className="w-40"
          />
          <Input
            label="Date To"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value)
              updateFilters({ dateTo: e.target.value })
            }}
            className="w-40"
          />
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load detections</p>
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              data={data?.data ?? []}
              keyExtractor={(d) => d.id}
              onRowClick={(d) => { navigate(`/detections/${d.id}`) }}
              isLoading={isLoading}
              emptyMessage="No detections found"
            />
            {data && data.total > DEFAULT_PAGE_SIZE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={data.total}
                itemsPerPage={DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </Card>

      {/* Resend Alert Confirmation */}
      <Modal
        isOpen={resendDetection !== null}
        onClose={() => { setResendDetection(null); setResendRecipients('') }}
        title="Resend Alert"
        footer={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => { setResendDetection(null); setResendRecipients('') }}
            >
              Cancel
            </Button>
            <Button
              isLoading={resendAlert.isPending}
              onClick={() => { resendAlert.mutate() }}
            >
              Resend
            </Button>
          </div>
        }
      >
        {resendDetection && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Resend alert for detection of <strong>{resendDetection.fakeFqdn}</strong> from
              canary <strong>{resendDetection.canaryName}</strong>.
            </p>
            <Input
              label="Recipients (optional, comma-separated)"
              name="resendRecipients"
              value={resendRecipients}
              onChange={(e) => { setResendRecipients(e.target.value) }}
              placeholder="email@example.com, admin@example.com"
            />
            <p className="text-xs text-gray-400">
              Leave empty to use the canary's default recipients.
            </p>
          </div>
        )}
      </Modal>
    </div>
  )
}

